import type { RouterConfig } from '@nuxt/schema'
import type { RouteRecordRaw } from 'vue-router'
import { regexPatterns, searchRouteTables } from '../../generated/routes'
import { RoutingDefinitions } from '../generator/types/RoutingDefinitions'
import { ROUTE_PREFIX } from '../generator/config/route-prefix'
import { checkIsMobile } from '@app/composables/useMobile';
import type { RouteLocationNormalized } from 'vue-router';
import { SearchType } from '@domain/enums/SearchType';
import { createSearchRouteResolvers } from '@app/routing/resolver/createSearchRouteResolvers';
import { registerSearchRouteResolvers } from '@app/routing/resolver/searchRouteRegistry';

// The generated search routes are matched by these resolvers instead of being registered in vue-router,
// which Nuxt recreates (and recompiles) for every request. Patterns are compiled lazily, once per process.
registerSearchRouteResolvers(createSearchRouteResolvers(searchRouteTables, regexPatterns, ROUTE_PREFIX))

const searchPage = (): Promise<any> => import('./pages/_gen.vue')

// One catch-all per search type. `searchRouteType` tells the resolvers which routes to match against.
// vue-router ranks the static `universities`/`scholarships` segments above the programme catch-all.
const searchCatchAllRoutes: RouteRecordRaw[] = [
	{
		name: 'ProgrammeSearch',
		path: `${ROUTE_PREFIX}/search/:searchPath(.*)*`,
		meta: { searchRouteType: SearchType.PROGRAMME_SEARCH },
		component: searchPage
	},
	{
		name: 'UniversitySearch',
		path: `${ROUTE_PREFIX}/search/universities/:searchPath(.*)*`,
		meta: { searchRouteType: SearchType.UNIVERSITY_SEARCH },
		component: searchPage
	},
	{
		name: 'ScholarshipSearch',
		path: `${ROUTE_PREFIX}/search/scholarships/:searchPath(.*)*`,
		meta: { searchRouteType: SearchType.SCHOLARSHIP_SEARCH },
		component: searchPage
	}
]

type RouteQueryValue = RouteLocationNormalized['query'][string];

const normalizeValue = (value: unknown): string | string[] => {
	if (Array.isArray(value)) return value.map(String);
	if (value == null) return '';
	// eslint-disable-next-line @typescript-eslint/no-base-to-string
	return String(value);
};

const normalizeRecord = (record: Record<string, unknown>): string => {
	const entries = Object.keys(record)
		.sort()
		.map((key) => [key, normalizeValue(record[key])] as const);
	return JSON.stringify(entries);
};

const getPageQuery = (route: RouteLocationNormalized): string | undefined => {
	const pageValue = route.query.page as RouteQueryValue;

	if (Array.isArray(pageValue)) return pageValue[0] || undefined;
	if (pageValue == null) return undefined;

	return String(pageValue);
};

const isPaginationOnlyChange = (to: RouteLocationNormalized, from: RouteLocationNormalized): boolean => {
	if (getPageQuery(to) === getPageQuery(from)) return false;

	const toQuery = Object.fromEntries(
		Object.entries(to.query as Record<string, unknown>)
			.filter(([key]) => key !== 'page')
	);
	const fromQuery = Object.fromEntries(
		Object.entries(from.query as Record<string, unknown>)
			.filter(([key]) => key !== 'page')
	);

	const querySameWithoutPage = normalizeRecord(toQuery) === normalizeRecord(fromQuery);

	const paramsSame = normalizeRecord(to.params) ===
		normalizeRecord(from.params);

	const routeSame = to.name === from.name && to.hash === from.hash;

	return querySameWithoutPage && paramsSame && routeSame;
};

export default {
	routes: (scanned) => {
		const catchAllRoute = scanned.find(route => route.path === '/:catchall(.*)*')
		const normalizedScanned = scanned.filter(route => route.path !== '/:catchall(.*)*')

		const finalRoutes = [
			...normalizedScanned,
			...searchCatchAllRoutes,
			...(catchAllRoute ? [{ ...catchAllRoute, name: RoutingDefinitions.CATCH_ALL }] : [])
		]

		return finalRoutes
	},

	scrollBehavior: (to, from, saved) => {
		if (checkIsMobile()) return { top: 0 };
		if (saved) return saved;

		return isPaginationOnlyChange(to, from) ? { top: 0 } : false;
	}
} as RouterConfig
